import React, { useState, useEffect } from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, getTabLabel, InlineEditableText, MdFilterList
} from './ThemeLayoutHelpers';
import { MdMenu, MdClose, MdArrowForward, MdDevices, MdOutlineSecurity, MdOutlineLocalShipping, MdOutlineVerified, MdBolt, MdLogin } from 'react-icons/md';

export default function OnePlusLayout({ p }) {
  const T = p.storeTheme || {};
  const buttonColor = T.buttonColor || "#eb0028"; // OnePlus Red
  const secondaryColor = T.secondaryColor || "#0f172a"; // Slate Dark

  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const copy = getHeroContent(p, {
    eyebrow: "Never Settle. Extreme Performance.",
    title: "OnePlus. Fast & Smooth.",
    subtitle: "Experience hyper-performance driven by Snapdragon 8 Elite, Hasselblad Camera Calibration, and 100W SuperVOOC Charge.",
    primaryCta: "Shop OnePlus",
    secondaryCta: "Learn More",
  });

  const navigateTo = (page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    if (page === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (page === "shop") {
      p.setActiveTab("products");
      setTimeout(() => {
        const el = document.getElementById("catalog-section");
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#0f172a] text-slate-200 antialiased" style={{ fontFamily: T.fontFamily || "Inter, sans-serif" }}>
      {/* Top Bold OnePlus-style banner */}
      <div className="w-full bg-[#eb0028] py-2.5 px-4 text-center text-xs font-bold uppercase tracking-widest text-white shadow-sm">
        <span className="inline-flex items-center gap-1.5">
          <MdBolt className="text-yellow-300 animate-pulse text-sm" /> NEVER SETTLE: Direct Checkout with free Express Shipping!
        </span>
      </div>

      {/* Premium Dark Nav */}
      <nav className={`sticky top-0 z-40 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#0f172a]/95 backdrop-blur shadow-2xl border-b border-slate-800" 
          : "bg-transparent border-b border-transparent"
      }`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border="border border-red-500/30 shadow-[0_0_15px_rgba(235,0,40,0.15)]" size="h-9 w-9" />
            <div>
              <p className="text-base font-extrabold tracking-tight text-white">{p.storeInfo?.businessName}</p>
              <p className="text-[9px] text-[#eb0028] font-bold tracking-widest uppercase">{p.storeInfo?.businessCategory || "OnePlus Elite Partner"}</p>
            </div>
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {[
              ["home", "Home"],
              ["shop", "Shop Catalog"],
              ["details", "Store Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`text-xs font-bold tracking-widest uppercase transition-all hover:text-[#eb0028] ${
                  activePage === page 
                    ? "text-[#eb0028] border-b-2 border-[#eb0028] pb-1" 
                    : "text-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Search, Wishlist, Cart */}
          <div className="flex items-center gap-3.5">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="hidden md:block w-48" />

            <button onClick={p.handleGoToWishlist} className="relative p-2 rounded-full hover:bg-slate-800 text-slate-300 transition-colors" title="Wishlist">
              <FaHeart className="text-base" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 text-[9px] font-bold rounded-full text-white flex items-center justify-center bg-[#eb0028] shadow-sm">
                  {p.wishlistCount}
                </span>
              )}
            </button>

            <CartBtn p={p} />

            {p.userMenuElement}

            <button 
              className="md:hidden p-2 rounded-full hover:bg-slate-800 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <MdClose size={20} /> : <MdMenu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#0f172a] border-b border-slate-800 shadow-xl p-6 flex flex-col gap-4 animate-fadeIn md:hidden">
            <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} dark className="w-full mb-2" />
            {[
              ["home", "Home"],
              ["shop", "Shop Catalog"],
              ["details", "Store Info"],
            ].map(([page, label]) => (
              <button
                key={page}
                onClick={(e) => page === "details" ? (p.setActiveTab("details"), navigateTo("about", e)) : navigateTo(page, e)}
                className={`py-2 text-left text-sm font-bold tracking-wider uppercase border-b border-slate-800 ${
                  activePage === page ? "text-[#eb0028] font-black" : "text-slate-400"
                }`}
              >
                {label}
              </button>
            ))}

            {/* Login Options for Mobile */}
            {!p.userProfile ? (
              <button
                onClick={() => {
                  p.handleOpenLogin();
                  setMobileMenuOpen(false);
                }}
                className="py-2 text-left text-sm font-bold tracking-wider uppercase border-b border-slate-800 flex items-center gap-2 text-[#eb0028]"
              >
                <MdLogin className="text-lg" />
                Login / Sign Up
              </button>
            ) : (
              <div className="py-2 border-b border-slate-800 flex flex-col gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[#eb0028] flex items-center justify-center text-xs font-black text-white shadow-md">
                    {p.userProfile?.name ? p.userProfile.name.charAt(0).toUpperCase() : "U"}
                  </div>
                  <div className="text-left">
                    <p className="text-xs font-bold text-white uppercase tracking-wider">{p.userProfile?.name}</p>
                    <p className="text-[10px] text-slate-400">{p.userProfile?.customer_mobile}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => {
                      p.handleViewProfile();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded transition-colors"
                  >
                    Profile
                  </button>
                  <button
                    onClick={() => {
                      p.handleViewOrders();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-slate-800 hover:bg-slate-700 px-3 py-1.5 rounded transition-colors"
                  >
                    Orders
                  </button>
                  <button
                    onClick={() => {
                      p.handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="text-[10px] font-extrabold uppercase tracking-wider text-white bg-[#eb0028] hover:bg-[#c80020] px-3 py-1.5 rounded transition-colors ml-auto shadow-sm"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        {activePage === "home" && (
          <>
            {/* Bold Hero Section */}
            <section className="w-full max-w-6xl mx-auto px-6 py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center text-left">
              <div className="flex flex-col items-start pr-0 md:pr-4">
                <span className="text-[#eb0028] text-xs font-black tracking-[0.25em] uppercase mb-3.5">
                  <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
                </span>
                <h1 className="text-5xl md:text-7xl font-black tracking-tight text-white leading-none mb-5 uppercase">
                  <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
                </h1>
                <p className="text-sm md:text-base text-slate-400 font-medium leading-relaxed mb-9 max-w-lg">
                  <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
                </p>
                <div className="flex gap-4">
                  <button 
                    onClick={() => navigateTo("shop")}
                    className="px-8 py-3 rounded-none text-xs font-extrabold uppercase tracking-widest text-white hover:opacity-90 active:scale-97 transition-all shadow-[0_4px_20px_rgba(235,0,40,0.35)]"
                    style={{ backgroundColor: buttonColor }}
                  >
                    <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
                  </button>
                  <button 
                    onClick={() => {
                      p.setActiveTab("details");
                      navigateTo("about");
                    }}
                    className="px-8 py-3 rounded-none text-xs font-extrabold uppercase tracking-widest border-2 border-slate-700 hover:border-slate-500 hover:text-white text-slate-300 transition-all"
                  >
                    <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
                  </button>
                </div>
              </div>

              {/* Visual Hero Showcase */}
              <div className="w-full rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 shadow-[0_20px_50px_rgba(235,0,40,0.04)] p-3">
                <HeroImage p={p} themeId="oneplus" className="h-[320px] md:h-[450px] rounded-xl overflow-hidden" />
              </div>
            </section>

            {/* Performance Spotlight */}
            <section className="bg-slate-950 border-y border-slate-900 py-16">
              <div className="max-w-6xl mx-auto px-6">
                <div className="text-center mb-12">
                  <span className="text-xs uppercase tracking-[0.2em] font-black text-[#eb0028]">Extreme Specs</span>
                  <h2 className="text-3xl font-extrabold tracking-tight text-white mt-1">Built to Never Settle</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                  {[
                    {
                      title: "100W SuperVOOC Charge",
                      desc: "Charge from 1% to 100% in just 26 minutes with dual-cell high-density battery safety.",
                      metric: "26 min",
                      sub: "Full Charge"
                    },
                    {
                      title: "Hasselblad Camera Co-tune",
                      desc: "State-of-the-art 50MP sensor calibrated for exact natural colors and studio portrait tone realism.",
                      metric: "Hasselblad",
                      sub: "Natural Colors"
                    },
                    {
                      title: "Snapdragon 8 Elite Processor",
                      desc: "Unbelievable rendering frame rates and hyper-speed multitasking with a customized cooling chamber.",
                      metric: "120 FPS",
                      sub: "Gaming Rate"
                    }
                  ].map((spec, i) => (
                    <div key={i} className="bg-slate-900 border border-slate-800/80 rounded-xl p-8 flex flex-col justify-between hover:border-slate-700 transition-colors duration-300">
                      <div>
                        <span className="text-[9px] tracking-widest font-black text-red-500 uppercase">SPEC SPOTLIGHT</span>
                        <h4 className="text-lg font-bold mt-1 text-white">{spec.title}</h4>
                        <p className="text-xs text-slate-400 mt-2.5 leading-relaxed">{spec.desc}</p>
                      </div>
                      <div className="mt-8 border-t border-slate-800 pt-4 flex items-baseline gap-2">
                        <span className="text-2xl font-black tracking-wider text-white uppercase">{spec.metric}</span>
                        <span className="text-[10px] font-bold text-slate-500 uppercase">{spec.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Value Highlights */}
            <section className="max-w-6xl mx-auto px-6 py-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { title: "Direct Store Warranty", icon: <MdOutlineSecurity className="text-2xl text-[#eb0028]" />, desc: "OnePlus warranty support" },
                  { title: "Genuine Guarantee", icon: <MdOutlineVerified className="text-2xl text-[#eb0028]" />, desc: "100% original stock parts" },
                  { title: "Insured Express Delivery", icon: <MdOutlineLocalShipping className="text-2xl text-[#eb0028]" />, desc: "Safe express courier tracking" },
                  { title: "Never Settle Service", icon: <MdDevices className="text-2xl text-[#eb0028]" />, desc: "Dedicated store service window" }
                ].map((b, idx) => (
                  <div key={idx} className="flex flex-col items-center text-center p-4 bg-slate-900 border border-slate-800 rounded-xl">
                    <div className="p-3 rounded-full bg-slate-950 mb-3">{b.icon}</div>
                    <p className="text-xs font-bold text-white">{b.title}</p>
                    <p className="text-[10px] text-slate-500 mt-1">{b.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Catalog Section */}
        <section id="catalog-section" className="max-w-6xl mx-auto px-6 py-12 scroll-mt-20">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div className="text-left">
              <span className="text-xs font-black uppercase tracking-widest text-[#eb0028]">STORE CATALOG</span>
              <h3 className="text-3xl font-extrabold text-white mt-1 uppercase">Explore Catalog</h3>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2.5">
              <button 
                onClick={() => p.setFilterPanelOpen(true)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-none border border-slate-850 bg-slate-900 hover:bg-slate-850 text-xs font-bold text-slate-300 transition-colors"
              >
                <MdFilterList size={16} /> Filter
              </button>
              
              <div className="rounded-none border border-slate-800 bg-slate-900 p-1 flex gap-1">
                {[
                  ["products", getTabLabel(p, "products")],
                  ["services", getTabLabel(p, "services")],
                  ["details", getTabLabel(p, "details")]
                ].map(([k, l]) => (
                  <button
                    key={k}
                    onClick={() => {
                      p.setActiveTab(k);
                      if (k === "details") {
                        navigateTo("about");
                      } else {
                        navigateTo("shop");
                      }
                    }}
                    className="px-4 py-1.5 rounded-none text-xs font-extrabold uppercase tracking-wider transition-all whitespace-nowrap"
                    style={p.activeTab === k ? { backgroundColor: buttonColor, color: "#fff" } : { color: "#94a3b8" }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full">
            <TabContent p={p} />
          </div>
        </section>
      </main>

      <StoreFooter p={p} />
    </div>
  );
}
